import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { format, startOfToday } from 'date-fns';
import { toast } from 'react-hot-toast';
import {
  GET_EMPLOYEE_VACATIONS,
  GET_TEAM_PTO_OVERVIEW,
} from '../../../graphql/team-availability-queries';
import {
  DELETE_EMPLOYEE_VACATION,
  REVIEW_EMPLOYEE_VACATION,
} from '../../../graphql/team-availability-mutations';
import AdminUserTimeOffFormModal from './AdminUserTimeOffFormModal';
import VacationApprovalBadge from '../../my-availability/VacationApprovalBadge';
import {
  compareCalendarDates,
  toCalendarDateKey,
  toLocalCalendarDate,
} from '../../../utils/availabilityDateUtils';

const VACATION_TYPE_LABELS = {
  PTO: 'PTO',
  SICK: 'Sick',
  PERSONAL: 'Personal',
  UNPAID: 'Unpaid',
  OTHER: 'Other',
};

function AdminUserTimeOffCard({ userId, requestId, onChange }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVacation, setEditingVacation] = useState(null);
  const [denyingVacation, setDenyingVacation] = useState(null);
  const [denialReason, setDenialReason] = useState('');

  const today = useMemo(() => startOfToday(), []);
  const horizon = useMemo(() => {
    const date = new Date(today);
    date.setFullYear(date.getFullYear() + 3);
    return date;
  }, [today]);

  const { data, loading, refetch } = useQuery(GET_EMPLOYEE_VACATIONS, {
    variables: requestId
      ? { userId }
      : { userId, startDate: today.toISOString(), endDate: horizon.toISOString() },
    skip: !userId,
    fetchPolicy: 'network-only',
  });
  const {
    data: ptoData,
    loading: ptoLoading,
    refetch: refetchPto,
  } = useQuery(GET_TEAM_PTO_OVERVIEW, {
    variables: { filters: { teamIds: [userId], limit: 1, offset: 0 } },
    skip: !userId,
    fetchPolicy: 'network-only',
  });
  const ptoDaysRemaining = ptoData?.teamPtoOverview?.items?.[0]?.ptoDaysRemaining;

  const [deleteEmployeeVacation, { loading: deleting }] = useMutation(DELETE_EMPLOYEE_VACATION, {
    onCompleted: () => {
      toast.success('Time off removed');
      refetch();
      refetchPto();
      onChange?.();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to remove time off');
    },
  });

  const [reviewEmployeeVacation, { loading: reviewing }] = useMutation(REVIEW_EMPLOYEE_VACATION, {
    onCompleted: (data) => {
      const status = data?.reviewEmployeeVacation?.approvalStatus;
      toast.success(status === 'APPROVED' ? 'Time off approved' : 'Time off denied');
      setDenyingVacation(null);
      setDenialReason('');
      refetch();
      refetchPto();
      onChange?.();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to review time off');
    },
  });

  const upcoming = useMemo(() => {
    const all = data?.employeeVacations || [];
    const todayKey = toCalendarDateKey(today);
    return [...all]
      .filter((entry) => entry.id === requestId || toCalendarDateKey(entry.endDate) >= todayKey)
      .sort((a, b) => compareCalendarDates(a.startDate, b.startDate));
  }, [data, requestId, today]);

  const handleAdd = () => {
    setEditingVacation(null);
    setIsModalOpen(true);
  };

  const handleEdit = (vacation) => {
    setEditingVacation(vacation);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingVacation(null);
  };

  const handleSaved = () => {
    handleCloseModal();
    refetch();
    refetchPto();
    onChange?.();
  };

  const handleDelete = (vacationId) => {
    if (!window.confirm('Remove this time-off entry?')) return;
    deleteEmployeeVacation({ variables: { id: vacationId } });
  };

  const handleApprove = (vacation) => {
    reviewEmployeeVacation({
      variables: { id: vacation.id, decision: 'APPROVE', reviewNote: null },
    });
  };

  const handleOpenDeny = (vacation) => {
    setDenyingVacation(vacation);
    setDenialReason('');
  };

  const handleDeny = (event) => {
    event.preventDefault();
    const reviewNote = denialReason.trim();
    if (!reviewNote) {
      toast.error('A denial reason is required');
      return;
    }
    reviewEmployeeVacation({
      variables: { id: denyingVacation.id, decision: 'DENY', reviewNote },
    });
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex items-center justify-between gap-3 mb-1">
          <h2 className="text-lg font-semibold text-gray-900">Upcoming Time Off</h2>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
              ptoDaysRemaining == null
                ? 'border-gray-200 bg-gray-100 text-gray-600'
                : 'border-violet-200 bg-violet-50 text-violet-800'
            }`}>
              PTO remaining: {ptoLoading
                ? 'Loading...'
                : ptoDaysRemaining == null
                  ? 'Not Set'
                  : `${ptoDaysRemaining} ${ptoDaysRemaining === 1 ? 'day' : 'days'}`}
            </span>
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Time Off
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Administrator entries are approved immediately. Employee requests remain off the calendar until approved.
        </p>

        {loading ? (
          <div className="py-8 text-center text-gray-500">Loading...</div>
        ) : upcoming.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            No upcoming time off scheduled. Click "Add Time Off" to add some.
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {upcoming.map((vacation) => {
              const start = toLocalCalendarDate(vacation.startDate);
              const end = toLocalCalendarDate(vacation.endDate);
              const isSameDay = toCalendarDateKey(start) === toCalendarDateKey(end);
              const typeLabel = VACATION_TYPE_LABELS[vacation.vacationType] || vacation.vacationType || 'Time Off';
              return (
                <li
                  key={vacation.id}
                  className={`py-3 px-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-md ${vacation.id === requestId ? 'ring-2 ring-sky-500 bg-sky-50' : ''}`}
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-900">
                      {isSameDay
                        ? format(start, 'EEE, MMM d, yyyy')
                        : `${format(start, 'EEE, MMM d')} – ${format(end, 'EEE, MMM d, yyyy')}`}
                    </span>
                    <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 mr-2">
                        {typeLabel}
                      </span>
                      <VacationApprovalBadge status={vacation.approvalStatus} />
                      {vacation.reason && <span className="text-gray-600">{vacation.reason}</span>}
                    </span>
                    {vacation.reviewNote && (
                      <span className="mt-1 text-xs text-gray-600">
                        Administrator note: {vacation.reviewNote}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {vacation.approvalStatus === 'PENDING' && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleApprove(vacation)}
                          disabled={reviewing}
                          className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOpenDeny(vacation)}
                          disabled={reviewing}
                          className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          Deny
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => handleEdit(vacation)}
                      className="text-sm font-medium text-sky-600 hover:text-sky-800"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(vacation.id)}
                      disabled={deleting}
                      className="text-sm font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <AdminUserTimeOffFormModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        userId={userId}
        vacation={editingVacation}
        onSaved={handleSaved}
      />

      {denyingVacation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Deny Time-Off Request</h3>
            <p className="mt-1 text-sm text-gray-600">
              Enter the reason that will be sent to the team member.
            </p>
            <form onSubmit={handleDeny} className="mt-4 space-y-4">
              <textarea
                value={denialReason}
                onChange={(event) => setDenialReason(event.target.value)}
                rows={4}
                required
                autoFocus
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-sky-500 focus:ring-2 focus:ring-sky-500"
                placeholder="Reason for denial"
              />
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setDenyingVacation(null)}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={reviewing || !denialReason.trim()}
                  className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {reviewing ? 'Saving...' : 'Deny Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUserTimeOffCard;
